import axios from "axios";
import { Address, AddressAutocompleteResponse } from "../Models/AddressAutocompleteModel";
import { extractAddress } from "../Helpers/AddressHelpers";

const AUTOCOMPLETE_URL = "https://autocomplete.norlys.dk/api/address/autocomplete";

export interface AddressRefinementOptions {
	startFrom?: "adgangsadresse" | "adresse";
	accessAddressId?: string;
}

const getAutocompleteUrl = () =>
	window._env_?.REACT_APP_AUTOCOMPLETE_URL?.trim() || AUTOCOMPLETE_URL;

const isSuggestion = (value: unknown): value is AddressAutocompleteResponse => {
	if (!value || typeof value !== "object") return false;
	const suggestion = value as Partial<AddressAutocompleteResponse>;
	const validEnvelope = (
		["vejnavn", "adgangsadresse", "adresse"].includes(suggestion.type ?? "") &&
		typeof suggestion.tekst === "string" &&
		typeof suggestion.forslagstekst === "string" &&
		typeof suggestion.caretpos === "number" &&
		Number.isFinite(suggestion.caretpos) &&
		suggestion.caretpos >= 0 &&
		!!suggestion.data && typeof suggestion.data === "object"
	);
	if (!validEnvelope) return false;
	if (suggestion.type === "adresse") return !!extractAddress(suggestion.type, suggestion.data!);
	if (suggestion.type === "adgangsadresse") {
		return typeof suggestion.data?.id === "string" && !!suggestion.data.id.trim();
	}
	return true;
};

export const fetchAddressById = async (id: string): Promise<Address | null> => {
	if (!id?.trim()) {
		return null;
	}

	try {
		const url = new URL(getAutocompleteUrl());
		url.searchParams.set("type", "adresse");
		url.searchParams.set("id", id);
		const response = await axios.get<unknown>(url.toString());
		if (!Array.isArray(response.data)) return null;
		for (const suggestion of response.data.filter(isSuggestion)) {
			const address = extractAddress(suggestion.type, suggestion.data);
			if (address?.id === id) return address;
		}
		return null;
	} catch {
		return null;
	}
};

export const fetchAddressSuggestions = async (
	query: string,
	caretpos?: number,
	refinement?: AddressRefinementOptions
): Promise<AddressAutocompleteResponse[]> => {
	if (!query || query.trim().length < 2) {
		return [];
	}

	try {
		const url = new URL(getAutocompleteUrl());
		url.searchParams.set("q", query);
		url.searchParams.set("type", "adresse");
		if (caretpos !== undefined) {
			url.searchParams.set("caretpos", String(caretpos));
		}
		if (refinement?.startFrom) {
			url.searchParams.set("startfra", refinement.startFrom);
		}
		if (refinement?.accessAddressId) {
			url.searchParams.set("adgangsadresseid", refinement.accessAddressId);
		}
		const response = await axios.get<unknown>(url.toString());
		return Array.isArray(response.data) ? response.data.filter(isSuggestion) : [];
	} catch {
		return [];
	}
};
